/**
 * @fileoverview Hook for managing browser local storage with type-safe get/set operations
 *
 * This module provides a hook that returns functions for interacting with localStorage,
 * handling JSON serialization/deserialization automatically. It provides type-safe
 * operations for getting, setting and removing items.
 *
 * @example
 * const [setItem, getItem, removeItem] = useLocalStorage();
 *
 * // Store a value
 * setItem('user', { id: 1, name: 'John' });
 *
 * // Retrieve the value
 * const user = getItem<{id: number, name: string}>('user');
 *
 * // Remove the value
 * removeItem('user');
 */

/**
 * @function useLocalStorage
 * @description Hook to manage browser local storage with type-safe get/set operations.
 * @returns {[<T>(key: string, value: T) => void, <T>(key: string) => T, (key: string) => void]} - Functions to set, get, and remove items from localStorage.
 */
export function useLocalStorage(): [
    <T>(key: string, value: T) => void,
    <T>(key: string) => T,
    (key: string) => void
] {
    function set<T>(key: string, value: T) {
        let v
        if (typeof value !== 'string') {
            v = JSON.stringify(value)
        } else {
            v = value
        }
        localStorage.setItem(key, v)
    }

    function get<T>(key: string): T | undefined {
        const v: string = localStorage.getItem(key)
        try {
            return JSON.parse(v)
        } catch (e: unknown) {
            return v as T
        }
    }

    function remove(key: string): void {
        localStorage.removeItem(key)
    }

    return [set, get, remove]
}
